/**
 * Header search placeholder typewriter animation.
 *
 * Usage:
 * <input data-animate-placeholder data-phrases="Search For X|Search For Y">
 */

const DEFAULT_TYPE_MS = 65;
const DEFAULT_DELETE_MS = 35;
const DEFAULT_HOLD_MS = 900;

/**
 * @param {string} raw
 */
function parsePhrases(raw) {
  if (!raw) return [];
  return raw
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * @param {HTMLInputElement} input
 */
function animatePlaceholder(input) {
  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const phrases = parsePhrases(input.dataset.phrases) || [];
  if (phrases.length === 0) return;

  if (prefersReducedMotion) {
    input.placeholder = phrases[0];
    return;
  }

  let phraseIndex = 0;
  let charIndex = 0;
  let deleting = false;
  let timer = /** @type {number | undefined} */ (undefined);

  const step = () => {
    // Stop animating once user types anything (just in case)
    if (input.value && input.value.length > 0) return;

    const phrase = phrases[phraseIndex];
    if (!deleting) {
      charIndex += 1;
      input.placeholder = phrase.slice(0, charIndex);
      if (charIndex >= phrase.length) {
        deleting = true;
        timer = window.setTimeout(step, DEFAULT_HOLD_MS);
        return;
      }
      timer = window.setTimeout(step, DEFAULT_TYPE_MS);
      return;
    }

    charIndex -= 1;
    input.placeholder = phrase.slice(0, Math.max(0, charIndex));
    if (charIndex <= 0) {
      deleting = false;
      phraseIndex = (phraseIndex + 1) % phrases.length;
      timer = window.setTimeout(step, 250);
      return;
    }
    timer = window.setTimeout(step, DEFAULT_DELETE_MS);
  };

  const start = () => {
    if (timer) window.clearTimeout(timer);
    phraseIndex = 0;
    charIndex = 0;
    deleting = false;
    step();
  };

  // Pause when not visible to avoid wasting cycles.
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      if (timer) window.clearTimeout(timer);
      timer = undefined;
    } else {
      start();
    }
  };

  document.addEventListener('visibilitychange', onVisibility, { passive: true });

  input.addEventListener('input', () => {
    if (input.value && input.value.length > 0) {
      if (timer) window.clearTimeout(timer);
      timer = undefined;
    } else {
      if (!timer) start();
    }
  });

  start();
}

document.addEventListener(
  'DOMContentLoaded',
  () => {
    /** @type {NodeListOf<HTMLInputElement>} */
    const inputs = document.querySelectorAll('input[data-animate-placeholder]');
    inputs.forEach((input) => animatePlaceholder(input));
  },
  { once: true }
);

